import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, MessageSquareText, Building2, ListChecks, CalendarCheck2 } from "lucide-react";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { PushSettings } from "../PushSettings";
import { fmtDate } from "./leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";

const PREF_CATEGORIES = [
  ["booking", "New bookings"], ["reminders", "Appointment reminders"],
  ["schedule_changes", "Schedule changes"], ["tech_updates", "Technician updates"],
  ["estimates", "Estimate updates"], ["payments", "Invoices & payments"],
  ["completion", "Service completion"], ["maintenance", "Maintenance reminders"],
  ["messages", "Customer messages"],
];

const BIZ_FIELDS = [
  ["company_name", "Company name"], ["short_name", "Short name (texts & pushes)"],
  ["phone", "Phone"], ["email", "Email"], ["website", "Website"],
  ["service_area", "Service area"], ["operating_hours", "Operating hours"],
  ["logo_url", "Logo URL"], ["booking_url", "Booking link"],
  ["google_profile_url", "Google profile URL"], ["google_review_url", "Google review link"],
];

export const GoogleCalendarCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/gcal/status`).then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/gcal/login`);
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Google sign-in");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await axios.post(`${API}/admin/gcal/disconnect`).catch(() => {});
    toast.success("Google Calendar disconnected");
    load();
  };

  if (!status) return null;
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6" data-testid="gcal-card">
      <div className="flex items-center gap-2 mb-3"><CalendarCheck2 className="h-5 w-5 text-blue-400" /><h3 className="font-heading font-semibold text-white">Google Calendar</h3></div>
      {!status.configured ? (
        <p className="text-amber-400/90 text-sm" data-testid="gcal-not-configured">Waiting on Google API credentials (Client ID &amp; Secret) — once added, connect here to show your Google Calendar bookings in the Appointments tab.</p>
      ) : status.connected ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-green-400" data-testid="gcal-connected">Connected as {status.email} — Google Calendar events now appear in the Appointments tab.</p>
          <button data-testid="gcal-disconnect-btn" onClick={disconnect} className="border border-zinc-700 text-zinc-400 hover:text-white rounded-sm px-4 py-2 text-xs font-semibold">Disconnect</button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-zinc-400">Connect garrett@masonsmobile.com to show your Google Calendar bookings in the Appointments tab.</p>
          <button data-testid="gcal-connect-btn" onClick={connect} disabled={busy} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-4 py-2 text-sm font-semibold">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Connect Google Calendar
          </button>
        </div>
      )}
    </div>
  );
};

export const SettingsTab = ({ onAuthError }) => {
  const [auto, setAuto] = useState(null);
  const [digest, setDigest] = useState(null);
  const [biz, setBiz] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [log, setLog] = useState([]);
  const [digestBusy, setDigestBusy] = useState(false);
  const [ownerAlerts, setOwnerAlerts] = useState(null);
  const [newAlert, setNewAlert] = useState({ phone: "", carrier: "verizon" });

  const fetchAll = useCallback(async () => {
    try {
      const [a, d, b, p, l, o] = await Promise.all([
        axios.get(`${API}/admin/settings/automation`),
        axios.get(`${API}/admin/settings/digest`),
        axios.get(`${API}/business/profile`),
        axios.get(`${API}/notify/prefs`),
        axios.get(`${API}/admin/notifications/log?limit=100`),
        axios.get(`${API}/admin/settings/owner-alerts`),
      ]);
      setAuto(a.data); setDigest(d.data); setBiz(b.data); setPrefs(p.data.prefs || {}); setLog(l.data);
      setOwnerAlerts(o.data);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Failed to load settings");
    }
  }, [onAuthError]);

  const saveOwnerAlerts = async (updates) => {
    try {
      const res = await axios.put(`${API}/admin/settings/owner-alerts`, updates);
      setOwnerAlerts((o) => ({ ...o, ...res.data }));
      toast.success("Text alert settings saved");
    } catch (err) {
      if (!onAuthError(err)) toast.error(err.response?.data?.detail || "Could not save alert settings");
    }
  };

  const addAlertRecipient = () => {
    if (!newAlert.phone.trim()) return;
    saveOwnerAlerts({ recipients: [...(ownerAlerts?.recipients || []), newAlert] });
    setNewAlert({ phone: "", carrier: "verizon" });
  };

  const testOwnerAlerts = async () => {
    try {
      const res = await axios.post(`${API}/admin/settings/owner-alerts/test`);
      if (res.data.success) toast.success(`Test text sent to ${res.data.sent} number(s)`);
      else toast.error(res.data.error || "Test failed");
    } catch {
      toast.error("Test failed");
    }
    fetchAll();
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveAuto = async (updates) => {
    try {
      await axios.put(`${API}/admin/settings/automation`, updates);
      toast.success("Automation settings saved");
    } catch (err) {
      if (!onAuthError(err)) toast.error(err.response?.data?.detail || "Could not save automation settings");
    }
    fetchAll();
  };

  const saveDigest = async (updates) => {
    try {
      await axios.put(`${API}/admin/settings/digest`, updates);
      toast.success("Digest settings saved");
    } catch (err) {
      if (!onAuthError(err)) toast.error(err.response?.data?.detail || "Could not save digest settings");
    }
    fetchAll();
  };

  const saveBiz = async () => {
    try {
      const res = await axios.put(`${API}/admin/business-profile`, biz);
      setBiz(res.data);
      toast.success("Business profile saved");
    } catch (err) {
      if (!onAuthError(err)) toast.error(err.response?.data?.detail || "Could not save business profile");
    }
  };

  const togglePref = async (key, channel) => {
    const cur = prefs[key] || {};
    const next = { ...prefs, [key]: { ...cur, [channel]: !(cur[channel] ?? true) } };
    setPrefs(next);
    try {
      await axios.put(`${API}/notify/prefs`, { prefs: next });
      toast.success("Notification preferences saved");
    } catch {
      toast.error("Could not save preferences");
    }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true);
    try {
      const res = await axios.post(`${API}/admin/settings/digest/send-now`);
      toast.success(`Digest sent (${res.data.delivery})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send digest");
    } finally {
      setDigestBusy(false);
      fetchAll();
    }
  };

  if (!auto || !digest || !biz || !prefs) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-tab">
      <PushSettings />

      <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6" data-testid="admin-notify-prefs-card">
        <div className="flex items-center gap-2 mb-4"><ListChecks className="h-5 w-5 text-blue-400" /><h3 className="font-heading font-semibold text-white">My Notification Preferences</h3></div>
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-5 gap-y-2 text-sm items-center">
          <div /><div className="text-zinc-500 text-xs">In-app</div><div className="text-zinc-500 text-xs">Push</div><div className="text-zinc-500 text-xs">Email</div>
          {PREF_CATEGORIES.map(([key, label]) => {
            const p = prefs[key] || {};
            return [
              <div key={`${key}-l`} className="text-zinc-300">{label}</div>,
              <Checkbox key={`${key}-a`} data-testid={`admin-pref-${key}-in_app`} checked={p.in_app ?? true} onCheckedChange={() => togglePref(key, "in_app")} />,
              <Checkbox key={`${key}-p`} data-testid={`admin-pref-${key}-push`} checked={p.push ?? true} onCheckedChange={() => togglePref(key, "push")} />,
              <Checkbox key={`${key}-e`} data-testid={`admin-pref-${key}-email`} checked={p.email ?? true} onCheckedChange={() => togglePref(key, "email")} />,
            ];
          })}
        </div>
      </div>

      <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6" data-testid="business-profile-card">
        <div className="flex items-center gap-2 mb-4"><Building2 className="h-5 w-5 text-blue-400" /><h3 className="font-heading font-semibold text-white">Business Profile</h3></div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          {BIZ_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label className="text-zinc-500 text-xs block mb-1">{label}</label>
              <Input data-testid={`biz-${key}-input`} className={inputCls} value={biz[key] || ""} onChange={(e) => setBiz((b) => ({ ...b, [key]: e.target.value }))} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-zinc-500 text-xs block mb-1">Service categories (comma separated)</label>
            <Input data-testid="biz-service-categories-input" className={inputCls} value={(biz.service_categories || []).join(", ")} onChange={(e) => setBiz((b) => ({ ...b, service_categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))} />
          </div>
        </div>
        <button data-testid="biz-save-btn" onClick={saveBiz} className="bg-blue-600 text-white rounded-sm px-4 py-2 text-xs font-semibold">Save Business Profile</button>
        <p className="text-zinc-600 text-[11px] mt-3">These details power every notification, email, and reminder template — nothing is hard-coded.</p>
      </div>

      <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6">
        <h3 className="font-heading font-semibold text-white mb-4">Automations</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <Checkbox data-testid="auto-confirmations-toggle" checked={auto.confirmations_enabled} onCheckedChange={(v) => saveAuto({ confirmations_enabled: v })} />
            Automatic appointment confirmation requests (push + in-app + email)
          </label>
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            Send confirmation request
            <Input data-testid="confirmation-hours-input" type="number" className={`${inputCls} !w-20 !h-8`} value={auto.confirmation_hours} onChange={(e) => setAuto((a) => ({ ...a, confirmation_hours: Number(e.target.value) }))} onBlur={() => saveAuto({ confirmation_hours: auto.confirmation_hours })} />
            hours before the appointment
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <Checkbox data-testid="auto-recurring-toggle" checked={auto.auto_send_recurring} onCheckedChange={(v) => saveAuto({ auto_send_recurring: v })} />
            Auto-send recurring maintenance reminders with your booking link when due
          </label>
        </div>
      </div>

      <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6" data-testid="owner-alerts-card">
        <div className="flex items-center gap-2 mb-4"><MessageSquareText className="h-5 w-5 text-blue-400" /><h3 className="font-heading font-semibold text-white">Booking Text Alerts</h3></div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer mb-4">
          <Checkbox data-testid="owner-alerts-toggle" checked={ownerAlerts?.enabled ?? true} onCheckedChange={(v) => saveOwnerAlerts({ enabled: v })} />
          Text me when someone books through the website form
        </label>
        <div className="space-y-2 mb-4">
          {(ownerAlerts?.recipients || []).map((r, i) => (
            <div key={`${r.phone}-${r.carrier}`} className="flex items-center gap-3 text-sm bg-[#0a0a0a] border border-zinc-800 rounded-sm px-4 py-2.5">
              <span className="text-white font-medium">{r.phone}</span>
              <span className="text-zinc-500 capitalize">{r.carrier === "tmobile" ? "T-Mobile" : r.carrier}</span>
              <button data-testid={`owner-alert-remove-${i}`} onClick={() => saveOwnerAlerts({ recipients: ownerAlerts.recipients.filter((_, j) => j !== i) })} className="ml-auto text-zinc-600 hover:text-red-400 text-xs">Remove</button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Input data-testid="owner-alert-phone-input" placeholder="Phone" className={`${inputCls} !w-40 !h-8`} value={newAlert.phone} onChange={(e) => setNewAlert((n) => ({ ...n, phone: e.target.value }))} />
          <select data-testid="owner-alert-carrier-select" className="h-8 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-2 text-sm" value={newAlert.carrier} onChange={(e) => setNewAlert((n) => ({ ...n, carrier: e.target.value }))}>
            {(ownerAlerts?.carriers || ["verizon", "tmobile", "att"]).map((c) => <option key={c} value={c}>{c === "tmobile" ? "T-Mobile" : c === "att" ? "AT&T" : c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <button data-testid="owner-alert-add-btn" onClick={addAlertRecipient} className="border border-zinc-700 text-white rounded-sm px-3 py-1.5 text-xs font-semibold">Add Number</button>
          <button data-testid="owner-alert-test-btn" onClick={testOwnerAlerts} className="border border-blue-500/50 text-blue-400 rounded-sm px-3 py-1.5 text-xs font-semibold">Send Test Text</button>
        </div>
        {!ownerAlerts?.email_configured && (
          <p className="text-amber-400/90 text-[11px]" data-testid="owner-alerts-pending-note">Waiting on the Gmail app password — alerts are configured and will start texting the moment it's added.</p>
        )}
        <p className="text-zinc-600 text-[11px] mt-1">Sent free via carrier email-to-text gateways. Delivery isn't guaranteed by carriers — in-app push alerts remain the most reliable backup.</p>
      </div>

      <GoogleCalendarCard />

      <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6" data-testid="digest-settings-card">
        <div className="flex items-center gap-2 mb-4"><MessageSquareText className="h-5 w-5 text-blue-400" /><h3 className="font-heading font-semibold text-white">Daily Digest</h3></div>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <Checkbox data-testid="digest-enabled-toggle" checked={digest.enabled} onCheckedChange={(v) => saveDigest({ enabled: v })} />
            Send a morning summary (today's jobs, unconfirmed tomorrow, due reminders, new leads)
          </label>
          <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-400">
            SMS number (when SMS is connected)
            <Input data-testid="digest-phone-input" className={`${inputCls} !w-44 !h-8`} value={digest.phone} onChange={(e) => setDigest((d) => ({ ...d, phone: e.target.value }))} onBlur={() => saveDigest({ phone: digest.phone })} />
            at
            <select data-testid="digest-hour-select" className="h-8 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-2 text-sm" value={digest.hour} onChange={(e) => saveDigest({ hour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{h === 0 ? "12:00 AM" : h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`}</option>
              ))}
            </select>
            (Las Vegas time)
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="digest-send-now" onClick={sendDigestNow} disabled={digestBusy} className="inline-flex items-center gap-1.5 border border-zinc-700 text-white rounded-sm px-3 py-2 text-xs font-semibold disabled:opacity-50">
              {digestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send Digest Now
            </button>
            <span className="text-xs text-zinc-500" data-testid="digest-last-sent">Last sent: {digest.last_sent_at ? fmtDate(digest.last_sent_at) : "—"}{digest.last_delivery ? ` via ${digest.last_delivery}` : ""}</span>
          </div>
          <p className="text-zinc-600 text-[11px]">Delivered via your notification bell, push, and email. SMS activates automatically when a texting provider is connected.</p>
        </div>
      </div>

      <div className="bg-[#121212] border border-zinc-800 rounded-xl p-6">
        <h3 className="font-heading font-semibold text-white mb-3">Notification Delivery Log</h3>
        {log.length === 0 ? <p className="text-zinc-600 text-xs">No deliveries yet.</p> : (
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 max-h-72 overflow-y-auto" data-testid="notification-log">
            {log.map((l) => (
              <div key={l.id} className="contents text-xs">
                <span className="text-zinc-300 border-b border-zinc-800/60 py-1.5 truncate" title={l.message}>{l.event} → {l.audience}</span>
                <span className="text-zinc-500 border-b border-zinc-800/60 py-1.5">{l.channel}</span>
                <span className={`border-b border-zinc-800/60 py-1.5 ${l.status === "sent" ? "text-green-400" : l.status === "skipped" ? "text-zinc-500" : "text-red-400"}`}>{l.status}{l.error ? ` — ${l.error}` : ""}{l.retries ? ` (retry ${l.retries})` : ""}</span>
                <span className="text-zinc-600 border-b border-zinc-800/60 py-1.5 text-right">{fmtDate(l.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
